import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Node } from '../../common/models/common/Node';
import { ITeam } from '../../common/models/team/ITeam';
import {
  pipe,
  map,
  filter,
  isEmpty,
  ifElse,
  identity,
  contains,
  toLower,
  partition,
  append
} from 'ramda';
import { IFormattedMessage } from '../../common/models/common/IFormattedMessage';
import { AssignmentFilterType } from '../../common/enums/AssignmentFilterType';
import { IUserInitials } from '../../common/models/user/IUserInitials';
import { User } from '../../common/models/user/User';

export interface IAssignmentFilterLabel {
  teams: IFormattedMessage;
  users: IFormattedMessage;
}

export const defaultLabels: IAssignmentFilterLabel = {
  teams: { id: 'teams', defaultMessage: 'Teams' },
  users: { id: 'users', defaultMessage: 'Users' }
};

export const nodeContainsLower = (input: string) => (node: Node) => contains(
  toLower(input),
  toLower(node.label as string)
);

const getTeamNode = (teams: ITeam[]) => ({
  label: <FormattedMessage {...defaultLabels.teams} />,
  value: 'Teams',
  children: map(team => ({
    label: team.name,
    value: team.id
  }), teams)
});

const getOtherUsersNode = (users: IUserInitials[]) => ({
  label: <FormattedMessage {...defaultLabels.users} />,
  value: 'Users',
  children: map(user => ({
    label: `${user.firstName} ${user.lastName}`,
    value: user.id
  }), users)
});

const nodeHasChildren = (node: Node) => Boolean(node.children?.length);

export const mapTree = (
  teams: ITeam[],
  users: IUserInitials[]
) => pipe(
  append(getTeamNode(teams)),
  append(getOtherUsersNode(users))
)([] as Node[]);

export const filterTree = (nodes: Node[], input: string) => pipe<Node[], Node[], Node[]>(
  map(({ children = [], ...rest }) => ({
    ...rest,
    children: pipe(
      filter<Node, 'array'>(nodeContainsLower(input)),
      ifElse(isEmpty, () => undefined, identity)
    )(children)
  } as Node)),
  filter(nodeHasChildren)
)(nodes);

export const getLabel = (radio: AssignmentFilterType, user?: User) => {
  let intlId;
  let defaultMessage;
  let additionalText;
  switch (radio) {
    case AssignmentFilterType.Me: {
      intlId = 'assignmentFilterBody.me';
      defaultMessage = 'Me';
      if (user) {
        additionalText = `(${user.firstName} ${user.lastName})`;
      }
      break;
    }
    case AssignmentFilterType.MyTeam: {
      intlId = 'assignmentFilterBody.myTeam';
      defaultMessage = 'My team';
      if (user) {
        additionalText = `(${user.teamName})`;
      }
      break;
    }
    case AssignmentFilterType.ShowAll: {
      intlId = 'assignmentFilterBody.showAll';
      defaultMessage = 'Show all';
      break;
    }
    case AssignmentFilterType.Custom: {
      intlId = 'assignmentFilterBody.custom';
      defaultMessage = 'Custom';
      break;
    }
    default:
  }
  return (
    <>
      <FormattedMessage id={intlId} defaultMessage={defaultMessage} />
      {additionalText ? ` ${additionalText}` : null}
    </>
  );
};

export const mapFilter = (checkedNodes: string[], userIds: string[]) => {
  const [users, teams] = partition(id => userIds.includes(id), checkedNodes);

  return ({
    userIds: users.length ? users : undefined,
    teamIds: teams.length ? teams : undefined
  });
};
